import { useEffect, useState } from "react";
import { adminBlog } from "@/lib/api";
import InfoproductRenderer from "@/components/InfoproductRenderer";
import { Sparkles, Plus, Pencil, Trash2, Eye, X, Wand2, Save, Loader2 } from "lucide-react";

const EMPTY = {
  title: "",
  subtitle: "",
  slug: "",
  meta_description: "",
  keywords: [],
  content: "",
  category: "Estrategia",
  cover_image: "",
  status: "draft",
  is_lead_magnet: false,
  lead_magnet_title: "",
  cta_label: "",
  cta_url: "",
  variables: {},
};

const GEN_DEFAULT = {
  tema_central: "Automatización de ventas inbound con IA",
  audiencia_objetivo: "Directores de Marketing y Consultores B2B",
  nivel_consciencia: "Consciente de la Solución",
  formato_documento: "Playbook Táctico & Workbook",
  oferta_backend: "Auditoría estratégica 1 a 1 de 30 minutos",
  tono_voz: "Pragmático, directo y de alta autoridad",
  numero_capitulos: "4 capítulos prácticos",
};

const NIVELES = [
  "Inconsciente",
  "Consciente del Problema",
  "Consciente de la Solución",
  "Consciente del Producto",
  "Totalmente Consciente",
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider font-bold mb-1.5 block text-zinc-600">{label}</label>
    {children}
  </div>
);

const input = "w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none";

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState(GEN_DEFAULT);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [preview, setPreview] = useState(false);

  const load = () => adminBlog.list().then(setPosts).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const newPost = () => { setForm(EMPTY); setEditingId(null); setPreview(false); setView("editor"); };
  const editPost = (p) => {
    setForm({ ...EMPTY, ...p, keywords: p.keywords || [] });
    setEditingId(p.id);
    setPreview(false);
    setView("editor");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (typeof payload.keywords === "string")
        payload.keywords = payload.keywords.split(",").map((k) => k.trim()).filter(Boolean);
      if (editingId) await adminBlog.update(editingId, payload);
      else await adminBlog.create(payload);
      await load();
      setView("list");
    } catch (e) {
      alert("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Borrar este artículo?")) return;
    await adminBlog.remove(id);
    load();
  };

  const generate = async () => {
    setGenerating(true);
    setGenError("");
    try {
      const r = await adminBlog.generate(genForm);
      setForm((f) => ({
        ...f,
        title: r.title || f.title,
        slug: r.slug || f.slug,
        meta_description: r.meta_description || f.meta_description,
        keywords: r.keywords && r.keywords.length ? r.keywords : f.keywords,
        content: r.content || f.content,
        lead_magnet_title: f.lead_magnet_title || `Descarga: ${r.title}`,
        cta_label: f.cta_label || genForm.oferta_backend,
        is_lead_magnet: true,
        variables: { ...genForm },
      }));
      setEditingId(null);
      setGenOpen(false);
      setView("editor");
    } catch (e) {
      setGenError(e?.response?.data?.detail || "Error generando el contenido. Intenta de nuevo.");
    } finally {
      setGenerating(false);
    }
  };

  const keywordsValue = Array.isArray(form.keywords) ? form.keywords.join(", ") : form.keywords;

  return (
    <div data-testid="admin-blog-panel">
      {view === "list" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-zinc-600">{posts.length} artículos. Genera infoproductos con IA y publícalos como recursos SEO + lead magnets.</p>
            <div className="flex gap-2">
              <button
                onClick={() => { setGenForm(GEN_DEFAULT); setGenError(""); setGenOpen(true); }}
                data-testid="ai-generate-open-btn"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white px-4 py-2.5 text-sm font-semibold transition-all"
              >
                <Wand2 className="w-4 h-4" /> Generar con IA
              </button>
              <button
                onClick={newPost}
                data-testid="new-post-btn"
                className="inline-flex items-center gap-2 border border-zinc-300 hover:border-zinc-900 px-4 py-2.5 text-sm font-semibold transition"
              >
                <Plus className="w-4 h-4" /> Nuevo
              </button>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-4 font-semibold">Título</th>
                  <th className="text-left p-4 font-semibold">Categoría</th>
                  <th className="text-left p-4 font-semibold">Estado</th>
                  <th className="text-left p-4 font-semibold">PDF</th>
                  <th className="text-left p-4 font-semibold">Vistas</th>
                  <th className="text-left p-4 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {posts.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-zinc-500">Aún no hay artículos. Genera el primero con IA.</td></tr>
                ) : (
                  posts.map((p) => (
                    <tr key={p.id} data-testid={`blog-admin-row-${p.slug}`} className="hover:bg-zinc-50">
                      <td className="p-4 font-medium max-w-xs">{p.title}</td>
                      <td className="p-4 text-zinc-600">{p.category}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 ${p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"}`}>
                          {p.status === "published" ? "Publicado" : "Borrador"}
                        </span>
                      </td>
                      <td className="p-4">{p.is_lead_magnet ? "✓" : "—"}</td>
                      <td className="p-4">{p.views || 0}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {p.status === "published" && (
                            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`view-post-${p.slug}`} className="p-1.5 hover:text-[#4F46E5]" title="Ver"><Eye className="w-4 h-4" /></a>
                          )}
                          <button onClick={() => editPost(p)} data-testid={`edit-post-${p.slug}`} className="p-1.5 hover:text-[#4F46E5]" title="Editar"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => del(p.id)} data-testid={`delete-post-${p.slug}`} className="p-1.5 hover:text-[#FF3B30]" title="Borrar"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "editor" && (
        <div className="space-y-5" data-testid="blog-editor">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-xl">{editingId ? "Editar artículo" : "Nuevo artículo"}</h3>
            <div className="flex gap-2">
              <button onClick={() => setPreview((v) => !v)} className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-sm font-semibold">
                <Eye className="w-4 h-4" /> {preview ? "Editar" : "Vista previa"}
              </button>
              <button onClick={() => setView("list")} className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-sm font-semibold">
                <X className="w-4 h-4" /> Cerrar
              </button>
              <button onClick={save} disabled={saving || !form.title || !form.content} data-testid="save-post-btn" className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>

          {preview ? (
            <div className="bg-white border border-zinc-200 p-6 md:p-8">
              <h1 className="font-display font-black text-3xl mb-6">{form.title}</h1>
              <InfoproductRenderer content={form.content} />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 space-y-4">
                <Field label="Título"><input className={input} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="post-title-input" /></Field>
                <Field label="Subtítulo"><input className={input} value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></Field>
                <Field label="Contenido (Markdown + bloques [BOX:]/[WORKSHEET])">
                  <textarea className={`${input} font-mono text-xs`} rows={18} value={form.content} onChange={(e) => set("content", e.target.value)} data-testid="post-content-input" />
                </Field>
              </div>
              <div className="space-y-4">
                <Field label="Estado">
                  <select className={input} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="post-status-select">
                    <option value="draft">Borrador</option>
                    <option value="published">Publicado</option>
                  </select>
                </Field>
                <Field label="Categoría"><input className={input} value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
                <Field label="Slug (URL)"><input className={input} value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto desde el título" /></Field>
                <Field label="Meta descripción (SEO)"><textarea className={input} rows={3} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} /></Field>
                <Field label="Palabras clave (separadas por coma)"><input className={input} value={keywordsValue} onChange={(e) => set("keywords", e.target.value)} /></Field>
                <Field label="Imagen de portada (URL)"><input className={input} value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} /></Field>
                <div className="border-t border-zinc-200 pt-4 space-y-4">
                  <label className="flex items-center gap-2 text-sm font-semibold">
                    <input type="checkbox" checked={form.is_lead_magnet} onChange={(e) => set("is_lead_magnet", e.target.checked)} data-testid="post-leadmagnet-checkbox" />
                    Lead magnet (descarga PDF con captura de email)
                  </label>
                  {form.is_lead_magnet && (
                    <Field label="Título del lead magnet"><input className={input} value={form.lead_magnet_title} onChange={(e) => set("lead_magnet_title", e.target.value)} /></Field>
                  )}
                  <Field label="CTA — texto del botón"><input className={input} value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} /></Field>
                  <Field label="CTA — enlace"><input className={input} value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://..." /></Field>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {genOpen && (
        <div className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !generating && setGenOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8" data-testid="ai-generator-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#4F46E5]" />
                <h3 className="font-display font-bold text-xl">Generar infoproducto con IA</h3>
              </div>
              <button onClick={() => !generating && setGenOpen(false)} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-zinc-500 mb-5">Gemini 3.8 Flash redacta un playbook/workbook completo listo para PDF y SEO.</p>

            <div className="space-y-4">
              <Field label="Tema central"><input className={input} value={genForm.tema_central} onChange={(e) => setGenForm({ ...genForm, tema_central: e.target.value })} data-testid="gen-tema-input" /></Field>
              <Field label="Audiencia objetivo / avatar"><input className={input} value={genForm.audiencia_objetivo} onChange={(e) => setGenForm({ ...genForm, audiencia_objetivo: e.target.value })} data-testid="gen-audiencia-input" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nivel de consciencia (Schwartz)">
                  <select className={input} value={genForm.nivel_consciencia} onChange={(e) => setGenForm({ ...genForm, nivel_consciencia: e.target.value })}>
                    {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </Field>
                <Field label="Número de capítulos"><input className={input} value={genForm.numero_capitulos} onChange={(e) => setGenForm({ ...genForm, numero_capitulos: e.target.value })} /></Field>
              </div>
              <Field label="Formato del documento"><input className={input} value={genForm.formato_documento} onChange={(e) => setGenForm({ ...genForm, formato_documento: e.target.value })} /></Field>
              <Field label="Oferta backend / próximo paso (CTA)"><input className={input} value={genForm.oferta_backend} onChange={(e) => setGenForm({ ...genForm, oferta_backend: e.target.value })} /></Field>
              <Field label="Tono y voz editorial"><input className={input} value={genForm.tono_voz} onChange={(e) => setGenForm({ ...genForm, tono_voz: e.target.value })} /></Field>
            </div>

            {genError && <p className="text-sm text-red-600 mt-4">{genError}</p>}

            <button
              onClick={generate}
              disabled={generating || !genForm.tema_central}
              data-testid="ai-generate-submit-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-6 py-3.5 font-semibold transition-colors disabled:opacity-60"
            >
              {generating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando (puede tardar ~1 min)...</> : <><Wand2 className="w-5 h-5" /> Generar documento</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
